/**
 * ShortcutTile — Tile individual do App Launcher
 *
 * Layout inspirado no launcher do Gmail: ícone grande centralizado + rótulo
 * curto abaixo. Suporta estado "edit" (jiggle + botão remover) e ação de
 * favoritar via estrela no canto superior direito.
 *
 * RELEASE: 2026-07-24 v1.1.0 - Altura fixa do tile para estabilizar as
 *   linhas do grid e permitir que o popover limite exatamente 3 linhas visíveis.
 * RELEASE: 2026-07-24 v1.0.0 - Criação.
 */
import { Star, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { AppShortcutDef } from '@/config/appShortcuts';

interface ShortcutTileProps {
  shortcut: AppShortcutDef;
  onNavigate: (route: string) => void;
  onToggleFavorite?: (key: string) => void;
  isFavorite?: boolean;
  editMode?: boolean;
  showFavoriteToggle?: boolean;
}

export function ShortcutTile({
  shortcut,
  onNavigate,
  onToggleFavorite,
  isFavorite = false,
  editMode = false,
  showFavoriteToggle = true,
}: ShortcutTileProps) {
  const Icon = shortcut.icon;

  return (
    <div
      className={cn(
        'group relative flex h-24 flex-col items-center justify-center gap-1.5 rounded-xl p-2 text-center transition-all',
        'hover:bg-muted/70 focus-within:bg-muted/70 cursor-pointer select-none',
        editMode && 'animate-wiggle',
      )}
      onClick={() => !editMode && onNavigate(shortcut.route)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (editMode) return;
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onNavigate(shortcut.route);
        }
      }}
      aria-label={shortcut.label}
    >
      {/* Botão remover (modo edição, apenas em favoritos) */}
      {editMode && onToggleFavorite && (
        <button
          type="button"
          className="absolute -right-1 -top-1 flex h-5 w-5 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow ring-1 ring-background hover:bg-destructive/90"
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(shortcut.key);
          }}
          aria-label={`Remover ${shortcut.label} dos favoritos`}
        >
          <X className="h-3 w-3" />
        </button>
      )}

      {/* Estrela (adicionar/remover favorito na lista completa) */}
      {!editMode && showFavoriteToggle && onToggleFavorite && (
        <button
          type="button"
          className={cn(
            'absolute right-1 top-1 rounded-md p-1 opacity-0 transition-opacity hover:bg-background/70 group-hover:opacity-100',
            isFavorite && 'opacity-100',
          )}
          onClick={(e) => {
            e.stopPropagation();
            onToggleFavorite(shortcut.key);
          }}
          aria-label={isFavorite ? 'Remover dos favoritos' : 'Adicionar aos favoritos'}
        >
          <Star
            className={cn(
              'h-3.5 w-3.5',
              isFavorite ? 'fill-amber-400 text-amber-500' : 'text-muted-foreground',
            )}
          />
        </button>
      )}

      <div
        className={cn(
          'flex h-11 w-11 items-center justify-center rounded-xl bg-muted/60 transition-colors group-hover:bg-background',
          shortcut.accent ?? 'text-primary',
        )}
      >
        <Icon className="h-6 w-6" strokeWidth={1.8} />
      </div>
      <span className="line-clamp-2 text-[11px] font-medium leading-tight text-foreground">
        {shortcut.label}
      </span>
    </div>
  );
}
